import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { History, Loader2, ShoppingCart, TriangleAlert } from "lucide-react";
import { api, apiError, fmt } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useStore } from "@/context/StoreContext";

const CHANGE_LABELS = {
  price: "Price updated",
  stock_reduced: "Less stock now",
  moq_raised: "MOQ raised",
  qty_step_adjusted: "Qty adjusted",
  out_of_stock: "Out of stock",
  unavailable: "No longer available",
};

export default function AbandonedCartRecovery() {
  const { user } = useAuth();
  const { refreshCart } = useStore();
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [restoring, setRestoring] = useState(false);

  const load = useCallback(() => {
    if (!user) { setRecord(null); setLoading(false); return; }
    api.get("/cart/recovery")
      .then(({ data }) => setRecord(data.record))
      .catch(() => setRecord(null))
      .finally(() => setLoading(false));
  }, [user]);
  useEffect(load, [load]);

  const restore = async () => {
    setRestoring(true);
    try {
      const { data } = await api.post("/cart/recovery/restore");
      await refreshCart();
      const n = data.restored.length;
      const skipped = data.skipped.length;
      const changed = data.restored.filter((r) => r.changes.length > 0).length;
      toast.success(
        `${n} item${n === 1 ? "" : "s"} back in your cart at today's prices` +
        (changed ? ` — ${changed} adjusted for latest price/stock` : "") +
        (skipped ? `. ${skipped} no longer available` : "")
      );
      setRecord(null);
    } catch (e) {
      toast.error(apiError(e, "Couldn't restore your cart"));
      load();
    } finally {
      setRestoring(false);
    }
  };

  if (!user || loading || !record) return null;

  return (
    <div className="mt-8 rounded-2xl border border-amber-500/25 bg-amber-500/[0.04] p-5 sm:p-6" data-testid="abandoned-cart-recovery">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-500/10 text-amber-400">
            <History className="h-5 w-5" />
          </span>
          <div>
            <h2 className="text-base font-bold text-1" data-testid="abandoned-cart-title">You left these behind</h2>
            <p className="mt-0.5 text-xs text-2" data-testid="abandoned-cart-meta">
              {record.item_count} item{record.item_count === 1 ? "" : "s"} · saved {new Date(record.abandoned_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}
              {" "}· was {fmt(record.value_at_abandonment)}, now {fmt(record.current_value)} at today's prices
            </p>
          </div>
        </div>
        <button onClick={restore} disabled={restoring || record.restorable === 0}
          data-testid="abandoned-cart-restore-btn"
          className="flex items-center gap-2 rounded-full bg-amber-500 px-5 py-2.5 text-xs font-bold text-black transition-colors hover:bg-amber-400 disabled:opacity-50">
          {restoring ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ShoppingCart className="h-3.5 w-3.5" />}
          Return to Cart
        </button>
      </div>

      {record.changed > 0 && (
        <p className="mt-3 flex items-center gap-1.5 text-[11px] font-semibold text-amber-400/90" data-testid="abandoned-cart-changed-note">
          <TriangleAlert className="h-3.5 w-3.5" />
          {record.changed} item{record.changed === 1 ? "" : "s"} changed since you left — we always restore at current price, stock and MOQ. No old prices, no surprises.
        </p>
      )}

      <div className="mt-4 space-y-2">
        {record.items.map((it) => (
          <div key={`${it.product_id}-${it.sku}`} data-testid={`abandoned-item-${it.sku}`}
            className={`flex items-center gap-3 rounded-xl border border-line bg-input px-3 py-2.5 ${it.available ? "" : "opacity-50"}`}>
            {it.image ? (
              <img src={it.image} alt="" className="h-10 w-10 shrink-0 rounded-lg border border-line bg-white object-contain" />
            ) : (
              <span className="h-10 w-10 shrink-0 rounded-lg border border-line bg-surface2" />
            )}
            <div className="min-w-0 flex-1">
              <p className="truncate text-xs font-bold text-1">{it.name} <span className="font-medium text-3">· {it.variant_label}</span></p>
              <p className="text-[11px] text-3">
                Qty {it.qty_requested.toLocaleString("en-IN")}
                {it.available && it.qty_final !== it.qty_requested && (
                  <span className="text-amber-400"> → {it.qty_final.toLocaleString("en-IN")}</span>
                )}
              </p>
            </div>
            <div className="shrink-0 text-right">
              {it.available ? (
                <>
                  <p className="text-xs font-bold text-1">{fmt(it.current_price)}</p>
                  {it.price_changed && it.price_at_abandonment != null && (
                    <p className="text-[10px] text-3 line-through">{fmt(it.price_at_abandonment)}</p>
                  )}
                </>
              ) : (
                <p className="text-[10px] font-bold uppercase text-red-400">{it.note || "Unavailable"}</p>
              )}
            </div>
            {it.changes.length > 0 && (
              <div className="hidden shrink-0 flex-col items-end gap-1 sm:flex">
                {it.changes.map((c) => (
                  <span key={c} className="rounded-full border border-amber-500/30 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-amber-400">
                    {CHANGE_LABELS[c] || c}
                  </span>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
